'use client';

import { useEffect } from 'react';
import { errorLogger } from '@/lib/errorLogger';

/** Next.js dev overlay requests these fallback chunks; in App Router-only they 404. Skip logging so the real first error is visible. */
const NEXT_FALLBACK_SCRIPT_PATTERN = /\/_next\/static\/chunks\/fallback\//;

/**
 * Global Error Handler Component
 *
 * Sets up global error handlers for:
 * - Unhandled errors
 * - Unhandled promise rejections
 * - React error boundaries (via ErrorBoundary component)
 */
export default function GlobalErrorHandler() {
  useEffect(() => {
    const handleError = (event: ErrorEvent) => {
      // Skip logging known Next.js fallback script 404s (see docs/TROUBLESHOOTING_SCRIPT_404.md)
      const scriptUrl =
        (event.target && 'src' in event.target ? String((event.target as HTMLScriptElement).src) : '') ||
        event.filename ||
        event.message ||
        '';
      if (NEXT_FALLBACK_SCRIPT_PATTERN.test(scriptUrl)) {
        return;
      }

      errorLogger.logError(
        event.error || new Error(event.message),
        {
          component: 'GlobalErrorHandler',
          function: 'handleError',
          errorType: 'unhandled',
          filename: event.filename,
          lineno: event.lineno,
          colno: event.colno
        },
        'high'
      );
    };

    // Handle unhandled promise rejections
    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      errorLogger.logError(
        event.reason,
        {
          component: 'GlobalErrorHandler',
          function: 'handleUnhandledRejection',
          errorType: 'unhandledRejection',
          promise: event.promise?.toString()
        },
        'high'
      );
    };

    // Add event listeners
    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    // Cleanup
    return () => {
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, []);

  return null; // This component doesn't render anything
}
